/**
 * Signature Requests collection — the real backing store the auditor said
 * was missing. A thin adapter over the EXISTING generic store (`db` from
 * `store.ts` — Firestore in prod, localStorage in dev), following the exact
 * documents/tasks collection pattern: ids and timestamps are minted HERE,
 * validation and state math live in the PURE engine
 * (`src/lib/crm/signatureRequest.ts`), and this file only persists.
 *
 * No new store interface, no schema drift: `signatureRequests` is a
 * registered collection in `db/types.ts` like every other.
 */

import { db, nowIso, uuid } from './store';
import {
  applyTransition,
  createRequest,
  type ConsentDisclosure,
  type SigEvent,
  type SignatureRequest,
} from '../crm/signatureRequest';

export interface NewSignatureRequestInput {
  docId: string;
  /** SHA-256 canonical doc hash — compute via crm/docHash.computeDocHash. */
  docHash: string;
  signerName: string;
  signerEmail: string;
  /** The exact disclosure text/version shown to the signer. */
  consentDisclosure: ConsentDisclosure;
}

/**
 * Mint + persist a new request in 'sent'. All invariants (non-empty hash,
 * disclosure, signer identity) are enforced by the pure engine's
 * `createRequest`, which throws before anything is written.
 */
export async function createSignatureRequest(
  input: NewSignatureRequestInput,
): Promise<SignatureRequest> {
  const record = createRequest({
    id: uuid(),
    docId: input.docId,
    docHash: input.docHash,
    signerName: input.signerName,
    signerEmail: input.signerEmail,
    consentDisclosure: input.consentDisclosure,
    sentAt: nowIso(),
  });
  await db.add('signatureRequests', record);
  return record;
}

export async function getSignatureRequest(
  id: string,
): Promise<SignatureRequest | null> {
  return db.get('signatureRequests', id);
}

/** Every request issued over one document, newest-first. */
export async function listSignatureRequestsByDoc(
  docId: string,
): Promise<SignatureRequest[]> {
  const all = await db.list('signatureRequests');
  return all
    .filter((r) => r.docId === docId)
    .sort((a, b) => (a.sentAt < b.sentAt ? 1 : -1));
}

/**
 * Patch a persisted request. Callers should derive the patch from the pure
 * engine (`applyTransition`) rather than hand-editing status — or use
 * {@link transitionSignatureRequest}, which does exactly that.
 */
export async function updateSignatureRequest(
  id: string,
  patch: Partial<SignatureRequest>,
): Promise<SignatureRequest> {
  return db.update('signatureRequests', id, patch);
}

/**
 * Load → applyTransition → persist, in one governed step. Illegal moves
 * come back `{ ok: false, error }` and write NOTHING; the store can never
 * hold a state the machine would reject.
 */
export async function transitionSignatureRequest(
  id: string,
  event: SigEvent,
): Promise<{ ok: boolean; record?: SignatureRequest; error?: string }> {
  const current = await db.get('signatureRequests', id);
  if (!current) {
    return { ok: false, error: `Signature request ${id} not found.` };
  }
  const result = applyTransition(current, event);
  if (!result.ok || !result.next) {
    return { ok: false, error: result.error };
  }
  const saved = await db.update('signatureRequests', id, result.next);
  return { ok: true, record: saved };
}

export function subscribeSignatureRequests(
  fn: (records: SignatureRequest[]) => void,
): () => void {
  return db.subscribe('signatureRequests', fn);
}
